/**
 * Regras da documentação de projeto (aba "Documentação" em ProjetoDetailView).
 *
 * Lógica pura, fora do componente, porque três coisas aqui são decisão de produto e não
 * detalhe de render: a ordem de leitura, o que conta como "projeto documentado" e o que é
 * um documento válido. A tela só desenha o resultado.
 */

import type { ProjetoDocumento, ProjetoDocumentoTipo } from '../../../types';

/** Extensões que o upload aceita. Executável e arquivo compactado ficam de fora. */
export const EXTENSOES_ACEITAS = [
  '.md', '.txt', '.pdf', '.xlsx', '.xls', '.csv',
  '.png', '.jpg', '.jpeg', '.gif', '.webp', '.svg',
  '.docx', '.pptx', '.json',
] as const;

/** 20 MB — o mesmo teto prático do bucket usado pelos anexos de tarefa. */
export const TAMANHO_MAXIMO_BYTES = 20 * 1024 * 1024;

/**
 * Bucket dos anexos de projeto. É PRIVADO: o download só sai por URL assinada, gerada na
 * hora, e a policy em `storage.objects` (migration 20260912160000) só libera para adm,
 * gerente e analista.
 *
 * Anexo de projeto carrega planilha de levantamento, relatório e print de sistema — coisas
 * que costumam ter nome de cliente. O bucket antigo `tarefas-anexos` é público (qualquer
 * um com a URL baixa, sem login), então ele deixou de ser o destino de anexo novo. Continua
 * sendo LIDO normalmente para os anexos que já foram enviados para lá.
 */
export const BUCKET_ANEXOS = 'projetos-anexos';

/** Papéis que podem baixar anexo de projeto — espelha `pode_baixar_anexo_projeto()`. */
export const PAPEIS_COM_ANEXO = ['adm', 'gerente', 'analista'] as const;

export function podeBaixarAnexo(papel?: string | null): boolean {
  return (PAPEIS_COM_ANEXO as readonly string[]).includes((papel || '').trim());
}

/**
 * Como distinguir os dois mundos sem coluna nova: o bucket público gravou a URL completa
 * (`https://...`), o privado grava só o CAMINHO do objeto. Se começa com http, é o legado
 * público e abre direto; senão, precisa de URL assinada.
 */
export function ehAnexoPrivado(arquivoUrl?: string | null): boolean {
  const v = (arquivoUrl || '').trim();
  return v.length > 0 && !/^https?:\/\//i.test(v);
}

/**
 * Ordem de leitura: `ordem` primeiro (é a intenção de quem documentou), `created_at` como
 * desempate. Deliberadamente NÃO agrupa por tipo — um anexo pode ser o item mais
 * importante ("comece pela planilha"), e agrupar por tipo tiraria essa escolha da pessoa.
 */
export function ordenarDocumentos(docs: ProjetoDocumento[]): ProjetoDocumento[] {
  return [...docs].sort(
    (a, b) => (a.ordem - b.ordem) || a.created_at.localeCompare(b.created_at)
  );
}

/** Próxima posição na lista, para um item novo entrar no fim em vez de no topo. */
export function proximaOrdem(docs: ProjetoDocumento[]): number {
  return docs.reduce((maior, d) => Math.max(maior, d.ordem), -1) + 1;
}

export interface ResumoDocumentacao {
  documentos: number;
  anexos: number;
  total: number;
  /**
   * Um projeto só conta como documentado se tiver ao menos um DOCUMENTO escrito. Anexo
   * sozinho não documenta nada: quem entra no projeto encontra uma planilha sem saber o
   * que ela responde. É essa a régua do aviso na etapa "concluído".
   */
  documentado: boolean;
}

export function resumirDocumentacao(docs: ProjetoDocumento[]): ResumoDocumentacao {
  const documentos = docs.filter(d => d.tipo === 'documento').length;
  const anexos = docs.filter(d => d.tipo === 'anexo').length;
  return { documentos, anexos, total: documentos + anexos, documentado: documentos > 0 };
}

/**
 * O projeto foi concluído sem deixar rastro de conhecimento? É o único momento em que a
 * tela cobra documentação — cobrar antes atrapalharia quem ainda está executando.
 */
export function faltaDocumentacaoAoConcluir(
  etapa: string,
  docs: ProjetoDocumento[]
): boolean {
  return etapa === 'concluido' && !resumirDocumentacao(docs).documentado;
}

export interface ValidacaoDocumento {
  ok: boolean;
  erro?: string;
}

/** Título e conteúdo obrigatórios: documento sem título vira uma linha anônima na lista. */
export function validarDocumento(titulo: string, conteudo: string): ValidacaoDocumento {
  if (!titulo.trim()) return { ok: false, erro: 'Dê um título ao documento.' };
  if (titulo.trim().length > 120) return { ok: false, erro: 'O título passou de 120 caracteres.' };
  if (!conteudo.trim()) return { ok: false, erro: 'O documento está vazio.' };
  return { ok: true };
}

const extensaoDe = (nome: string): string => {
  const i = nome.lastIndexOf('.');
  return i === -1 ? '' : nome.slice(i).toLowerCase();
};

export function validarArquivo(nome: string, tamanho: number): ValidacaoDocumento {
  const ext = extensaoDe(nome);
  if (!ext) return { ok: false, erro: 'O arquivo precisa ter extensão.' };
  if (!(EXTENSOES_ACEITAS as readonly string[]).includes(ext)) {
    return { ok: false, erro: `Formato ${ext} não é aceito aqui.` };
  }
  if (tamanho <= 0) return { ok: false, erro: 'O arquivo está vazio.' };
  if (tamanho > TAMANHO_MAXIMO_BYTES) {
    return { ok: false, erro: `O arquivo passou de ${formatarTamanho(TAMANHO_MAXIMO_BYTES)}.` };
  }
  return { ok: true };
}

export function formatarTamanho(bytes?: number | null): string {
  if (!bytes || bytes < 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${Math.round(kb)} KB`;
  return `${(kb / 1024).toFixed(1).replace('.', ',')} MB`;
}

/**
 * Um `.md` colado ou enviado como arquivo é documento, não anexo — o ponto da tela é que
 * o texto seja LIDO nela, e um .md guardado como anexo volta a ser arquivo para baixar,
 * que é justamente o problema que este módulo resolve.
 */
export function tipoSugeridoPara(nomeArquivo: string): ProjetoDocumentoTipo {
  const ext = extensaoDe(nomeArquivo);
  return ext === '.md' || ext === '.txt' ? 'documento' : 'anexo';
}

/**
 * Título a partir do nome do arquivo: tira a extensão e troca separador por espaço.
 * `PLAN-boleto-unico-fase1.md` vira `PLAN boleto unico fase1`, que é editável e melhor do
 * que deixar o campo vazio esperando que a pessoa digite.
 */
export function tituloSugeridoPara(nomeArquivo: string): string {
  const ext = extensaoDe(nomeArquivo);
  const base = ext ? nomeArquivo.slice(0, -ext.length) : nomeArquivo;
  return base.replace(/[-_]+/g, ' ').replace(/\s+/g, ' ').trim() || nomeArquivo;
}

/**
 * Caminho no bucket. Prefixo por projeto para a limpeza de um projeto apagado ser um
 * `remove` de pasta, e timestamp para dois arquivos de mesmo nome não se sobrescreverem.
 */
export function caminhoNoBucket(projetoId: number, nomeArquivo: string, agora = Date.now()): string {
  const seguro = nomeArquivo.replace(/[^\w.\-]+/g, '_');
  return `projetos/${projetoId}/${agora}-${seguro}`;
}
